/**
 * Pre-computed URL slugs for each `CATEGORY_GROUPS` key, used to disambiguate
 * `/doc/<categorySlug>` (category landing page) from `/doc/<docId>` (doc
 * detail page) on the edge.
 *
 * **Why this is its own file**: the CloudFront Functions edge bundle has a
 * ≤4 KB budget per handler (see `docs/projects/active/a-b-test-public-doc.md`
 * §6, the router cache policy in `packages/infra/src/web/cdn.ts`). Importing from `./public-doc-categories` pulls
 * the entire `CATEGORY_GROUPS` object (~3 KB of subcategory arrays) into
 * the bundle. Splitting the slug list out keeps the edge bundle lean while
 * still letting `./public-doc-categories` re-export both forms for app-side
 * consumers.
 *
 * `PUBLIC_DOC_CATEGORY_SLUGS_PATTERN` is the pipe-joined regex-alternation
 * form (e.g. `health-and-medicine|biology-and-natural-sciences|...`); the
 * edge bundle imports only this and feeds it to `new RegExp(...)`. That
 * saves ~30 bytes of array-literal punctuation vs. importing the array and
 * `.join('|')`-ing at module init — the edge bundle is held to a tight
 * margin (~4 bytes spare) and this matters.
 *
 * Both are kept in sync with `Object.keys(CATEGORY_GROUPS).map(slugifyTitle)`
 * via the parity test at
 * `apps/start/test/unit/analytics/public-doc-category-slugs.test.ts`. If
 * you add or rename a category in `./public-doc-categories`, that test
 * fails — update both here in lockstep.
 */
export const PUBLIC_DOC_CATEGORY_SLUGS: ReadonlyArray<string> = [
  'health-and-medicine',
  'biology-and-natural-sciences',
  'psychology',
  'social-sciences',
  'environmental-studies',
  'humanities',
  'technology-and-engineering',
  'business-and-finance',
  'law-and-legal-studies',
  'education',
  'mathematics-and-statistics',
  'physical-sciences',
  'communication-and-media',
  'self-help-and-personal-development',
  'fiction-and-creative-writing',
  'government-and-policy',
  'interdisciplinary-studies',
  'other',
];

/**
 * Same slugs as `PUBLIC_DOC_CATEGORY_SLUGS`, joined with `|` for direct use
 * inside a regex pattern. See file-level comment for why both forms exist.
 */
export const PUBLIC_DOC_CATEGORY_SLUGS_PATTERN =
  'health-and-medicine|biology-and-natural-sciences|psychology|social-sciences|environmental-studies|humanities|technology-and-engineering|business-and-finance|law-and-legal-studies|education|mathematics-and-statistics|physical-sciences|communication-and-media|self-help-and-personal-development|fiction-and-creative-writing|government-and-policy|interdisciplinary-studies|other';
